import cx from "classnames";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { noop } from "common/utils/noop";

import PillButton from "client/common/components/buttons/PillButton";
import { urls } from "client/routes";

import { iconForCategorySlug } from "../constants/categoryIcons";
import { ColorScheme } from "../constants/colorSchemes";
import { schemeForCategoryIndex } from "../utils";
import { IClientBrowseCategory } from "../types";
import { DEALS_CATEGORY } from "./CategoryCard";

import styles from "../styles/CategoryCard.module.less";

// CategoryCardV2 reads only id / slug / name / description from category, matching
// CategoryCard so it accepts the carousel-display category without the heavier
// product_lines payload. `index` is the 0-based cycle position used to pick the color.
type CategoryCardV2Category = Pick<IClientBrowseCategory, "id" | "slug" | "name" | "description">;

type CategoryCardV2Props = {
  category: CategoryCardV2Category;
  index: number;
  className?: string;
};

const gradientFor = (scheme: ColorScheme) =>
  `linear-gradient(to top, ${scheme.gradientStart} 50%, ${scheme.gradientStop} 100%)`;

type BlobProps = {
  fill: string;
  className?: string;
};

const Blob = ({ fill, className }: BlobProps) => (
  <svg
    width="173"
    height="173"
    viewBox="0 0 173 173"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M116.063 1.47163C132.393 5.38294 148.005 14.0635 157.78 27.5324C167.362 40.7358 166.705 57.899 168.708 74.0102C170.758 90.5096 176.709 107.772 169.628 122.86C162.475 138.1 145.666 145.715 131.023 154.304C116.252 162.968 101.364 173.591 84.1861 172.974C66.969 172.356 52.7777 160.815 38.7276 150.992C24.7123 141.192 8.81511 131.892 2.76339 116.044C-3.2435 100.313 1.70937 82.8955 6.57304 66.7859C11.0535 51.9456 18.3266 38.144 29.553 27.3101C40.4982 16.7476 54.5657 10.7016 69.2275 6.3225C84.5609 1.74279 100.494 -2.25742 116.063 1.47163Z"
      fill={fill}
    />
  </svg>
);

const getCategoryDestination = (category: CategoryCardV2Category) => {
  if (category.slug === DEALS_CATEGORY.slug) {
    return urls.products.deals();
  }
  return urls.products.category({ params: { id: category.id } });
};

// The small "popular categories" strip keeps the white card background (per product);
// only the blob and icon take the position-based scheme color. The full section card
// (below) carries the scheme gradient.
const CategoryCardV2Small = ({ category, index, className }: CategoryCardV2Props) => {
  const scheme = schemeForCategoryIndex(index);

  return (
    <LinkButton
      className={cx(styles.small, category.slug === DEALS_CATEGORY.slug && styles.deals, className)}
      key={category.id}
      dest={getCategoryDestination(category)}
    >
      <div className={styles.name}>{category.name}</div>
      <div>
        <Blob fill={scheme.blob} className={styles.blob} />
        <Icon
          className={styles.icon}
          icon={iconForCategorySlug(category.slug)}
          color={scheme.icon}
        />
      </div>
    </LinkButton>
  );
};

const CategoryCardV2 = ({ category, index, className }: CategoryCardV2Props) => {
  const scheme = schemeForCategoryIndex(index);

  return (
    <LinkButton
      dest={getCategoryDestination(category)}
      className={cx(styles.full, className)}
      style={{ background: gradientFor(scheme) }}
    >
      <div className={styles.content}>
        <div className={styles.name}>{category.name}</div>
        <div className={styles.description}>{category.description}</div>
        <PillButton size="xs" variant="secondary" onClick={noop}>
          View all
        </PillButton>
      </div>
      <div>
        <Blob fill={scheme.blob} className={styles.blob} />
        <Icon
          className={styles.icon}
          icon={iconForCategorySlug(category.slug)}
          color={scheme.icon}
        />
      </div>
    </LinkButton>
  );
};

export default Object.assign(CategoryCardV2, {
  Small: CategoryCardV2Small,
});
